import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/hooks/use-language';
import { useToast } from '@/hooks/use-toast';
import { getGroupColorClasses, getLocalizedGroupName } from '@/utils/groupColors';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Member {
  id: number;
  name: string;
  phone: string;
  email: string;
  groups: string[];
  subgroups: string[];
  isAdmin: boolean;
}

interface DeleteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member | null;
  onDeleteMember: (memberId: number) => void;
}

export function DeleteMemberDialog({ open, onOpenChange, member, onDeleteMember }: DeleteMemberDialogProps) {
  const { t, language } = useLanguage();
  const { toast } = useToast();

  const handleDelete = () => {
    if (member) {
      onDeleteMember(member.id);
      onOpenChange(false);
      toast({
        title: t("members.memberDeleted"),
        description: t("members.memberDeletedDesc", { name: member.name }),
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  if (!member) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <DialogTitle>{t("members.deleteMember")}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {t("members.deleteConfirmation", { name: member.name })}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Member Info */}
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <div className="font-medium">{member.name}</div>
            <div className="text-sm text-muted-foreground">{member.email}</div>
            <div className="text-sm text-muted-foreground">{member.phone}</div>
            
            {/* Admin Badge */}
            <div className="flex items-center gap-2 pt-2">
              {member.isAdmin && (
                <Badge variant="admin" className="text-xs">
                  {getLocalizedGroupName('Administratorer / Admins', language)}
                </Badge>
              )}
            </div>

            {/* Groups */}
            {member.groups.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">{t("members.groups")}:</div>
                <div className="flex flex-wrap gap-1">
                  {member.groups.map(group => {
                    const colors = getGroupColorClasses(group);
                    return (
                      <Badge 
                        key={group} 
                        variant="outline" 
                        className={cn("text-xs border-transparent", colors.bg, colors.text)}
                      >
                        {getLocalizedGroupName(group, language)}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Subgroups */}
            {member.subgroups.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">{t("members.subgroups")}:</div>
                <div className="flex flex-wrap gap-1">
                  {member.subgroups.map(subgroup => {
                    const colors = getGroupColorClasses(subgroup);
                    return (
                      <Badge 
                        key={subgroup} 
                        variant="outline" 
                        className="text-xs"
                        style={{
                          borderColor: `hsl(var(--group-${colors.variant}))`,
                          color: `hsl(var(--group-${colors.variant}))`
                        }}
                      >
                        {getLocalizedGroupName(subgroup, language)}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="text-sm text-muted-foreground">
            {t("members.deleteWarning")}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            {t("members.deleteMember")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
